import { inject, Injectable } from '@angular/core';
import { CanMatch, Route, Router, UrlSegment } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take, timeout } from 'rxjs/operators';

import { OPC_EXPERIMENT_TEST_VARIANT } from '@sondermind/constants';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey } from '@sondermind/data-access/feature-flags';

/**
 * Route guard that grants access to the one-page checkout route only when the OPC
 * retest experiment (MER-4944) buckets the visitor into the OPC test variant.
 * Falls back to false on timeout (1s) to avoid blocking navigation. Always returns
 * false for internal users so the QuickCheckout modal is still available to
 * support / enterprise-card coordinators via `?internal=true`.
 */
@Injectable({ providedIn: 'root' })
export class OnePageCheckoutFeatureFlagGuard implements CanMatch {
  private featureFlagService = inject(FEATURE_FLAG_SERVICE);
  private router = inject(Router);

  canMatch(_route: Route, _segments: UrlSegment[]): Observable<boolean> {
    const queryParams = this.router.getCurrentNavigation()?.extractedUrl.queryParams ?? {};
    if (queryParams['internal'] === 'true') {
      return of(false);
    }

    return this.featureFlagService
      .evaluate$<string>(FeatureFlagKey.ONE_PAGE_CHECKOUT_EXPERIMENT_V2, '')
      .pipe(
        take(1),
        timeout({ first: 1000, with: () => of(null) }),
        map((variant) => variant?.key === OPC_EXPERIMENT_TEST_VARIANT),
      );
  }
}
